/* eslint-disable max-len */
/* eslint-disable no-restricted-syntax */
import React, { useContext, useEffect, useState } from 'react';
import * as qs from 'query-string';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Loading } from '../../../components/Loading';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { SUCCESS } from '../../../shared/Constants';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { DEFAULT_STATE } from '../helpers/dashboard.helper';
import { DASHBOARD_QUERY } from '../services/dashboard.service';
import CompanyInformationDifferentFromBillingInformationDialog from '../components/dashboard/CompanyInformationDifferentFromBillingInformationDialog';
import SubscriptionExpiredDialog from '../components/dashboard/SubscriptionExpiredDialog';
import DocumentsToAccept from '../components/documents/DocumentsToAccept';
import AccountToBeDeletedNotification from '../components/documents/AccountToBeDeletedNotification';
import DeclinedDocuments from '../components/documents/DeclinedDocuments';
import EssentialAccountToBeDeletedNotification from '../components/essential-accounts/EssentialAccountToBeDeletedNotification';
import AccountOrganisationSizeNotification from '../components/essential-accounts/AccountOrganisationSizeNotification';
import AccountNoOrganisationSizeNotification from '../components/essential-accounts/AccountNoOrganisationSizeNotification';
import GenericDashboard from '../components/dashboard/GenericDashboard';

const i18n = defineMessages({
  done: {
    id: 'account.subscription.done',
    defaultMessage: 'Done',
  },
  accountActivated: {
    id: 'account.subscription.account-activated',
    defaultMessage: 'Your NoLimit account is activated, and your organization can start using GR8PI!',
  },
});

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(DEFAULT_STATE);

  const location = useLocation();
  const { purchaseSubscription } = qs.parse(location.search);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const { loading, error, data: queryData } = useQuery(DASHBOARD_QUERY, { variables: { accountId: currentAccount } });

  useEffect(() => {
    if (queryData.account) {
      const {
        id,
        type,
        isHeadOfFamily,
        currentCreditPlan,
        creditPlanExpirationDate,
        companyInformation,
        subscriptionInformation,
        gpiRepresentativeData,
        dashboard,
        respondentData,
        isCompanyInformationDifferentFromBillingInformation,
      } = queryData.account;

      const subscriptionExpiredDialogVisible = subscriptionInformation
        ? subscriptionInformation.currentSubscription
          ? !subscriptionInformation.hasActiveSubscription &&
            new Date() > new Date(subscriptionInformation.currentSubscription.endsOn)
          : false
        : false;

      setDashboardData({
        accountData: {
          id,
          type,
          isHeadOfFamily,
          currentCreditPlan,
          creditPlanExpirationDate,
          companyInformation,
          subscriptionInformation,
          gpiRepresentativeData,
        },
        dashboard,
        respondentData,
        subscriptionExpiredDialogVisible,
        isCompanyInformationDifferentFromBillingInformation,
      });
    }
  }, [queryData]);

  useEffect(() => {
    if (purchaseSubscription === SUCCESS) {
      const message = (
        <>
          <p>
            <strong>{formatMessage(i18n.done)}</strong>
          </p>
          <p>{formatMessage(i18n.accountActivated)}</p>
        </>
      );

      growlActionSuccess(growl, message);
    }
    // eslint-disable-next-line
  }, [purchaseSubscription]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const {
    accountData,
    subscriptionExpiredDialogVisible,
    isCompanyInformationDifferentFromBillingInformation,
  } = dashboardData;
  const { subscriptionInformation, gpiRepresentativeData } = accountData;

  return (
    <>
      <SubscriptionExpiredDialog
        subscriptionInformation={subscriptionInformation}
        gpiRepresentativeData={gpiRepresentativeData}
        visible={subscriptionExpiredDialogVisible}
        hideDialog={() => {
          setDashboardData((previousState) => {
            return {
              ...previousState,
              subscriptionExpiredDialogVisible: false,
            };
          });
        }}
      />
      <CompanyInformationDifferentFromBillingInformationDialog
        visible={isCompanyInformationDifferentFromBillingInformation}
      />
      <DocumentsToAccept />
      <DeclinedDocuments />
      <AccountToBeDeletedNotification />
      <EssentialAccountToBeDeletedNotification />
      <AccountOrganisationSizeNotification />
      <AccountNoOrganisationSizeNotification />
      <GenericDashboard dashboardData={dashboardData} />
    </>
  );
};

export default Dashboard;
